import axios from 'axios';
import type {
  IntegrationEndpointDto,
  CreateEndpointRequest,
  UpdateEndpointRequest,
  InvokeRequest,
  InvokeResultDto,
  FileBackendInfoDto,
  FileUploadResultDto,
  PresignResultDto,
  CallLogListItem,
  CallLogDetailResponse,
  PageResponse,
  EndpointListFilter,
  CallLogFilter,
  TcpConsoleStatusDto,
  TcpEchoRequest,
  TcpEchoResultDto,
  GrpcHealthDto,
  GrpcEchoRequest,
  GrpcEchoResultDto,
} from '../types/integrationHub';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

/** 바이너리 다운로드 전용 (파일 다운로드) */
function binaryGet(path: string): Promise<{ data: Blob; filename: string }> {
  const token = getStoredToken();
  return axios
    .get(`${import.meta.env.VITE_API_BASE ?? '/api/eraf'}${path}`, {
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    .then((res) => {
      const disposition = (res.headers['content-disposition'] as string) ?? '';
      const match = /filename[^;=\n]*=["']?([^"';\n]+)["']?/.exec(disposition);
      const filename = match?.[1]?.trim() ?? 'download';
      return { data: res.data as Blob, filename };
    });
}

export const integrationHubApi = {
  // =========================================================================
  // 엔드포인트 CRUD
  // =========================================================================

  /** GET /integration-hub/endpoints — 목록 (INTEGRATION_READ) */
  listEndpoints: (
    filters: EndpointListFilter
  ): Promise<PageResponse<IntegrationEndpointDto>> => {
    const sp = new URLSearchParams();
    if (filters.protocol) sp.set('protocol', filters.protocol);
    if (filters.enabled !== '' && filters.enabled !== undefined)
      sp.set('enabled', String(filters.enabled));
    if (filters.page !== undefined) sp.set('page', String(filters.page));
    if (filters.size !== undefined) sp.set('size', String(filters.size));
    if (filters.sort) sp.set('sort', filters.sort);
    return apiClient
      .get<unknown, { data: PageResponse<IntegrationEndpointDto> }>(
        `/integration-hub/endpoints?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /integration-hub/endpoints/{id} — 상세 (INTEGRATION_READ) */
  getEndpoint: (id: number): Promise<IntegrationEndpointDto> =>
    apiClient
      .get<unknown, { data: IntegrationEndpointDto }>(`/integration-hub/endpoints/${id}`)
      .then((res) => res.data),

  /** POST /integration-hub/endpoints — 생성 (INTEGRATION_MANAGE) */
  createEndpoint: (body: CreateEndpointRequest): Promise<IntegrationEndpointDto> =>
    apiClient
      .post<unknown, { data: IntegrationEndpointDto }>('/integration-hub/endpoints', body)
      .then((res) => res.data),

  /** PUT /integration-hub/endpoints/{id} — 수정 (INTEGRATION_MANAGE) */
  updateEndpoint: (
    id: number,
    body: UpdateEndpointRequest
  ): Promise<IntegrationEndpointDto> =>
    apiClient
      .put<unknown, { data: IntegrationEndpointDto }>(
        `/integration-hub/endpoints/${id}`,
        body
      )
      .then((res) => res.data),

  /** DELETE /integration-hub/endpoints/{id} — soft-delete (INTEGRATION_MANAGE) */
  deleteEndpoint: (id: number): Promise<void> =>
    apiClient
      .delete<unknown, { data: null }>(`/integration-hub/endpoints/${id}`)
      .then(() => undefined),

  // =========================================================================
  // 연동 호출 (Invoke)
  // =========================================================================

  /** POST /integration-hub/endpoints/{id}/invoke — 호출 (INTEGRATION_INVOKE) */
  invokeEndpoint: (id: number, body: InvokeRequest): Promise<InvokeResultDto> =>
    apiClient
      .post<unknown, { data: InvokeResultDto }>(
        `/integration-hub/endpoints/${id}/invoke`,
        body
      )
      .then((res) => res.data),

  // =========================================================================
  // 스토리지 어댑터 (FileManager SPI 시연)
  // =========================================================================

  /** GET /integration-hub/storage/backend — 현재 backend 정보 (INTEGRATION_READ) */
  getStorageBackend: (): Promise<FileBackendInfoDto> =>
    apiClient
      .get<unknown, { data: FileBackendInfoDto }>('/integration-hub/storage/backend')
      .then((res) => res.data),

  /** POST /integration-hub/storage/files — 파일 업로드 (INTEGRATION_INVOKE) */
  uploadFile: (file: File): Promise<FileUploadResultDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = getStoredToken();
    return axios
      .post<unknown>(
        `${import.meta.env.VITE_API_BASE ?? '/api/eraf'}/integration-hub/storage/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      )
      .then((res) => {
        const d = res.data as Record<string, unknown>;
        if (d && d.success === true) return d['data'] as FileUploadResultDto;
        return Promise.reject({
          code: (d['error'] as Record<string, unknown>)?.['code'] ?? 'UPLOAD_FAILED',
          message: (d['error'] as Record<string, unknown>)?.['message'] ?? '업로드 실패',
        });
      });
  },

  /** GET /integration-hub/storage/files/{fileId} — 파일 다운로드 (INTEGRATION_READ) */
  downloadFile: (fileId: string): Promise<{ data: Blob; filename: string }> =>
    binaryGet(`/integration-hub/storage/files/${fileId}`),

  /** POST /integration-hub/storage/files/{fileId}/presign — presign URL (INTEGRATION_INVOKE) */
  presignFile: (fileId: string): Promise<PresignResultDto> =>
    apiClient
      .post<unknown, { data: PresignResultDto }>(
        `/integration-hub/storage/files/${fileId}/presign`
      )
      .then((res) => res.data),

  // =========================================================================
  // 호출 이력
  // =========================================================================

  /** GET /integration-hub/call-logs — 이력 목록 (INTEGRATION_READ) */
  listCallLogs: (filters: CallLogFilter): Promise<PageResponse<CallLogListItem>> => {
    const sp = new URLSearchParams();
    if (filters.endpointCode) sp.set('endpointCode', filters.endpointCode);
    if (filters.protocol) sp.set('protocol', filters.protocol);
    if (filters.result) sp.set('result', filters.result);
    if (filters.minStatus !== '' && filters.minStatus !== undefined)
      sp.set('minStatus', String(filters.minStatus));
    if (filters.from) sp.set('from', filters.from);
    if (filters.to) sp.set('to', filters.to);
    if (filters.traceId) sp.set('traceId', filters.traceId);
    if (filters.page !== undefined) sp.set('page', String(filters.page));
    if (filters.size !== undefined) sp.set('size', String(filters.size));
    if (filters.sort) sp.set('sort', filters.sort);
    return apiClient
      .get<unknown, { data: PageResponse<CallLogListItem> }>(
        `/integration-hub/call-logs?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /integration-hub/call-logs/{id} — 이력 상세 (INTEGRATION_READ) */
  getCallLog: (id: number): Promise<CallLogDetailResponse> =>
    apiClient
      .get<unknown, { data: CallLogDetailResponse }>(`/integration-hub/call-logs/${id}`)
      .then((res) => res.data),

  // =========================================================================
  // TCP 콘솔 (C061 — §Δ.2)
  // =========================================================================

  /** GET /integration-hub/tcp/status — 콘솔 상태 (INTEGRATION_READ) */
  getTcpStatus: (): Promise<TcpConsoleStatusDto> =>
    apiClient
      .get<unknown, { data: TcpConsoleStatusDto }>('/integration-hub/tcp/status')
      .then((res) => res.data),

  /** POST /integration-hub/tcp/echo — echo 왕복 (INTEGRATION_INVOKE) */
  tcpEcho: (body: TcpEchoRequest): Promise<TcpEchoResultDto> =>
    apiClient
      .post<unknown, { data: TcpEchoResultDto }>('/integration-hub/tcp/echo', body)
      .then((res) => res.data),

  // =========================================================================
  // gRPC 콘솔 (C062 — §Δ.2)
  // =========================================================================

  /** GET /integration-hub/grpc/health — Health 조회 (INTEGRATION_READ) */
  getGrpcHealth: (): Promise<GrpcHealthDto> =>
    apiClient
      .get<unknown, { data: GrpcHealthDto }>('/integration-hub/grpc/health')
      .then((res) => res.data),

  /** POST /integration-hub/grpc/echo — echo (unary/streaming) (INTEGRATION_INVOKE) */
  grpcEcho: (body: GrpcEchoRequest): Promise<GrpcEchoResultDto> =>
    apiClient
      .post<unknown, { data: GrpcEchoResultDto }>('/integration-hub/grpc/echo', body)
      .then((res) => res.data),
};
